import { useContext, useMemo } from 'react';

import { createIndexFromTree, filterTree, transformTree } from '@futrli/shared-services';
import { ConsolidatedForecastDetail } from '@futrli-sdk/consolidations';
import { Forecast, ThisNumberExplainedRow } from '@futrli-sdk/predict';

import { ConsolidatedForecastReport } from '../../../../../core/types';
import { ForecastTree } from '../../types';
import { getCombinedForecastTrees, isUnusedNode, sortPredictionChildren } from './utils';
import { UserGridSettingsContext } from '../../../reports/user-grid-settings/user-grid-settings-context';
import { recalculateForecastTree } from '../../utils/forecast-tree';

/**
 * Combines and processes the forecast trees for the given forecast report, applying any user settings
 * such as expanded rows and name overrides
 * @param forecastReport - The forecast report to process and extract the data from
 * @param accountNameMap - A mapping of node refs to overridden account names (optional)
 */
export const useProcessedForecastTrees = (
  forecast: Forecast | ConsolidatedForecastDetail | null | undefined,
  forecastReport: ConsolidatedForecastReport | null | undefined,
  accountNameMap: Record<string, string> = {},
  thisNumberExplainedMap: Record<string, ThisNumberExplainedRow> | undefined
) => {
  const { getShowZeroValueAccounts } = useContext(UserGridSettingsContext);

  const trees = useMemo(() => getCombinedForecastTrees(forecastReport), [forecastReport]);

  // We get a few different trees from the backend, balance_sheet Profit_and_loss etc.
  // We'll filter and process those trees a little further here
  const filteredTrees = useMemo(() => {
    const output: Record<string, ForecastTree> = {};

    Object.keys(trees).forEach(key => {
      // Filter out any unused nodes from the tree (unless user has set to show zero value accounts)
      let filteredTree = filterTree(trees[key].tree, node => {
        return !isUnusedNode(node) || getShowZeroValueAccounts(forecast?.id || '');
      });

      // Apply one-time recalculations / overrides to each node
      filteredTree = transformTree(filteredTree, node => ({
        ...node,
        childCount: node.children?.length || 0, // childCount is one-time as it should count collapsed nodes
        name: accountNameMap[node.ref] || node.name,
        thisNumberExplained: thisNumberExplainedMap?.[node.ref]
      }));

      // Recalculate calculated fields to ensure tree data is accurate
      filteredTree = recalculateForecastTree(filteredTree);

      // Sort prediction children alphabetically by name
      filteredTree = sortPredictionChildren(filteredTree);

      output[key] = {
        tree: filteredTree,
        index: createIndexFromTree(filteredTree, { idProp: 'ref' })
      };
    });

    return output;
  }, [accountNameMap, forecast?.id, getShowZeroValueAccounts, thisNumberExplainedMap, trees]);

  return filteredTrees;
};
